/**
 * Cookie management for Star Modal
 * Shows modal on 3rd and 9th videos only, or until user clicks "don't show again"
 */

const STAR_MODAL_DONT_SHOW_COOKIE_NAME = 'star_modal_dont_show'
const STAR_MODAL_3RD_SHOWN_COOKIE_NAME = 'star_modal_3rd_shown'
const STAR_MODAL_VIDEO_SWIPE_COOKIE_NAME = 'star_modal_video_swipe_count'
const STAR_MODAL_9TH_SHOWN_COOKIE_NAME = 'star_modal_9th_shown'
const VIDEO_SWIPE_TRIGGER_COUNT_3RD = 3
const VIDEO_SWIPE_TRIGGER_COUNT_9TH = 9

/**
 * Check if the 3rd video modal has been shown
 * @returns {boolean} True if 3rd video modal has been shown
 */
export function getThirdModalShown() {
  try {
    const flag = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${STAR_MODAL_3RD_SHOWN_COOKIE_NAME}=`))
      ?.split('=')[1]
    
    return flag === 'true'
  } catch (error) {
    console.warn('Error reading 3rd modal shown cookie:', error)
    return false
  }
}

/**
 * Set the 3rd video modal as shown
 */
export function setThirdModalShown() {
  try {
    // Set cookie to expire in 30 days
    const expires = new Date()
    expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000))
    
    document.cookie = `${STAR_MODAL_3RD_SHOWN_COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/`
  } catch (error) {
    console.warn('Error setting 3rd modal shown cookie:', error)
  }
}

/**
 * Check if the modal should be shown
 * @returns {boolean} True if modal should be shown
 */
export function shouldShowStarModal() {
  // Check if user has clicked "don't show again"
  const dontShow = getDontShowAgainFlag()
  if (dontShow) {
    console.log('Star Modal: User has set dont show again flag')
    return false
  }
  
  const thirdModalShown = getThirdModalShown()
  const videoSwipeCount = getStarModalVideoSwipeCount()
  const ninthModalShown = getNinthModalShown()
  

  // Show modal only in two cases:
  // 1. On 3rd video - when videoSwipeCount equals 3 and thirdModalShown is false
  // 2. On 9th video - when videoSwipeCount equals 9 and ninthModalShown is false
  const shouldShow = (videoSwipeCount === VIDEO_SWIPE_TRIGGER_COUNT_3RD && !thirdModalShown) || (videoSwipeCount === VIDEO_SWIPE_TRIGGER_COUNT_9TH && !ninthModalShown)
  
  console.log('Star Modal Cookie Check:', {
    thirdModalShown,
    videoSwipeCount,
    videoSwipeTrigger3rd: VIDEO_SWIPE_TRIGGER_COUNT_3RD,
    videoSwipeTrigger9th: VIDEO_SWIPE_TRIGGER_COUNT_9TH,
    ninthModalShown,
    shouldShow,
    dontShow
  })
  
  return shouldShow
}

/**
 * Get the current video swipe count from cookie
 * @returns {number} Current video swipe count
 */
export function getStarModalVideoSwipeCount() {
  try {
    const count = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${STAR_MODAL_VIDEO_SWIPE_COOKIE_NAME}=`))
      ?.split('=')[1]
    
    return count ? parseInt(count, 10) : 0
  } catch (error) {
    console.warn('Error reading star modal video swipe cookie:', error)
    return 0
  }
}

/**
 * Increment the video swipe count cookie
 * @returns {number} New video swipe count
 */
export function incrementStarModalVideoSwipeCount() {
  try {
    const currentCount = getStarModalVideoSwipeCount()
    const newCount = currentCount + 1
    
    // Set cookie to expire in 30 days
    const expires = new Date()
    expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000))
    
    document.cookie = `${STAR_MODAL_VIDEO_SWIPE_COOKIE_NAME}=${newCount}; expires=${expires.toUTCString()}; path=/`
    
    return newCount
  } catch (error) {
    console.warn('Error setting star modal video swipe cookie:', error)
    return 0
  }
}

/**
 * Check if the 9th video modal has been shown
 * @returns {boolean} True if 9th video modal has been shown
 */
export function getNinthModalShown() {
  try {
    const flag = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${STAR_MODAL_9TH_SHOWN_COOKIE_NAME}=`))
      ?.split('=')[1]
    
    return flag === 'true'
  } catch (error) {
    console.warn('Error reading 9th modal shown cookie:', error)
    return false
  }
}

/**
 * Set the 9th video modal as shown
 */
export function setNinthModalShown() {
  try {
    // Set cookie to expire in 30 days
    const expires = new Date()
    expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000))
    
    document.cookie = `${STAR_MODAL_9TH_SHOWN_COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/`
  } catch (error) {
    console.warn('Error setting 9th modal shown cookie:', error)
  }
}

/**
 * Get the "don't show again" flag from cookie
 * @returns {boolean} True if user clicked "don't show again"
 */
export function getDontShowAgainFlag() {
  try {
    const flag = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${STAR_MODAL_DONT_SHOW_COOKIE_NAME}=`))
      ?.split('=')[1]
    
    return flag === 'true'
  } catch (error) {
    console.warn('Error reading dont show again cookie:', error)
    return false
  }
}


/**
 * Set the "don't show again" flag
 */
export function setDontShowAgainFlag() {
  try {
    // Set cookie to expire in 1 year
    const expires = new Date()
    expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000))
    
    document.cookie = `${STAR_MODAL_DONT_SHOW_COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/`
  } catch (error) {
    console.warn('Error setting dont show again cookie:', error)
  }
}

/**
 * Reset all star modal cookies (for testing)
 */
export function resetStarModalCookies() {
  try {
    document.cookie = `${STAR_MODAL_DONT_SHOW_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    document.cookie = `${STAR_MODAL_3RD_SHOWN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    document.cookie = `${STAR_MODAL_VIDEO_SWIPE_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    document.cookie = `${STAR_MODAL_9TH_SHOWN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
  } catch (error) {
    console.warn('Error resetting star modal cookies:', error)
  }
}
